/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import Button from '@react/react-spectrum/Button';
import { TabView, Tab } from '@react/react-spectrum/TabView';
import './appInformation.scss';
import Compatibility from './compatibility';
import Languages from './languages';
import Versions from './versions';
import TranslationUtil from '../../utils/translation';
import { getPathData } from '../../utils/urlUtils';
import { InformationDialogAnalytics } from '../../analytics';
import { ANALYTICS } from '../../utils/enums';

const ACTION_HANDLER_TYPES = {
    TAB_SELECT: 'tab-select',
    BUTTON_CLICK: 'button-click',
    VIEW_MORE_CLICK: 'view-more-click',
    LTS_CLICK: 'long-term-supported-click',
};

class AppInformation extends Component {
    componentDidMount() {
        const { data: { app } } = this.props;
        /*
         * Since information dialog can be
         * opened from pdp and catalog,
         * determine its source.
         */
        InformationDialogAnalytics.setInformationDialogSource(
            getPathData().isPDP
                ? ANALYTICS.PDP
                : ANALYTICS.APPS
        );
        // Information dialog render event
        InformationDialogAnalytics.informationDialogRendered(app);
    }

    getSelectedTabIndex() {
        const {
            data:
                {
                    compatibility,
                    languages,
                    version,
                    defaultSelectedTabId,
                },
        } = this.props;
        if (defaultSelectedTabId) {
            const tabListIDs = [];
            const tabList = [compatibility, languages, version];
            tabList.forEach((item) => {
                if (item && item.tabId) {
                    tabListIDs.push(item.tabId);
                }
            });
            return tabListIDs.indexOf(defaultSelectedTabId);
        }
        return 0;
    }

    actionHandler(e, type, ...args) {
        const { data: { onAction, tabSelectActionId } } = this.props;
        const guid = uuid.v4();
        let actionParam;
        switch (type) {
        case ACTION_HANDLER_TYPES.TAB_SELECT:
            actionParam = {
                actionObject: args[0],
                guid,
            };
            onAction(
                e, tabSelectActionId, actionParam
            );
            break;
        case ACTION_HANDLER_TYPES.BUTTON_CLICK:
            const doneButtonObj = args[0];
            actionParam = {
                actionObject: doneButtonObj,
                guid,
            };
            onAction(
                e, doneButtonObj.actionId, actionParam
            );
            break;
        case ACTION_HANDLER_TYPES.VIEW_MORE_CLICK:
            const viewMoreObj = args[0];
            actionParam = {
                actionObject: viewMoreObj,
                guid,
            };
            onAction(
                e, viewMoreObj.actionId, actionParam
            );
            break;
        case ACTION_HANDLER_TYPES.LTS_CLICK:
            const ltsObj = args[0];
            actionParam = {
                actionObject: ltsObj,
                guid,
            };
            onAction(
                e, ltsObj.actionId, actionParam
            );
            break;
        default:
        }
    }

    renderTabContent(tabItem) {
        const data = { ...this.props };
        data.tabData = tabItem;

        switch (tabItem.tabId) {
        case 'compatibility':
            return (<Compatibility data={data} actionHandler={this.actionHandler.bind(this)} />);
        case 'languages':
            return (<Languages data={data} actionHandler={this.actionHandler.bind(this)} />);
        case 'version':
            return (<Versions data={data} actionHandler={this.actionHandler.bind(this)} />);
        default:
            return null;
        }
    }

    renderInformationTabs(tabList) {
        const tabItems = [];
        for (let i = 0; i < tabList.length; i += 1) {
            if (tabList[i] && tabList[i].tabId) {
                tabItems.push(
                    <Tab key={tabList[i].tabId} className="information-modal-tab" label={TranslationUtil.getMessage(`ccx.app-info-dialog.${tabList[i].tabId}`)}>
                        {this.renderTabContent(tabList[i])}
                    </Tab>
                );
            }
        }
        return tabItems;
    }

    renderFooter(actionList) {
        const buttonList = [];
        actionList.forEach((button) => {
            buttonList.push(
                <Button
                    key={button.actionId}
                    className="information-modal-done-button"
                    onClick={(e) => {
                        this.actionHandler(e, ACTION_HANDLER_TYPES.BUTTON_CLICK, button);
                    }}
                    label={TranslationUtil.getMessage(`ccx.app-info-dialog.${button.actionId}`)}
                    variant="cta" />
            );
        });
        return buttonList;
    }

    render() {
        const {
            data: {
                compatibility, languages, version, actionList, app,
            },
        } = this.props;
        const applicableTabList = [];
        const tabList = [compatibility, languages, version];
        tabList.forEach((item) => {
            if (item && item.tabId) {
                applicableTabList.push(item);
            }
        });
        return (
            <div className="info-dialog-content">
                <TabView
                    defaultSelectedIndex={this.getSelectedTabIndex() || 0}
                    className="information-modal"
                    orientation="vertical"
                    onSelect={(itemNum) => {
                        InformationDialogAnalytics.informationDialogTabClick(
                            app,
                            applicableTabList[itemNum]
                        );
                        this.actionHandler(
                            {}, ACTION_HANDLER_TYPES.TAB_SELECT, applicableTabList[itemNum]
                        );
                    }}>
                    {this.renderInformationTabs(applicableTabList)}
                </TabView>
                <div className="information-modal-footer">
                    {this.renderFooter(actionList)}
                </div>
            </div>
        );
    }
}

export default observer(AppInformation);
